// src/api/useApi.ts

import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from 'react-query';
import axios, { AxiosRequestConfig } from 'axios';
import axiosClient, { API_VALIDATION_ERROR_EVENT } from './axios-client';

export { API_PERMISSION_DENIED_EVENT, API_VALIDATION_ERROR_EVENT } from './axios-client';

/**
 * Interface représentant une erreur d'API.
 */
export interface ApiError {
    message: string;
    isAuthError?: boolean;
    response?: {
        data: {
            message: string;
            errors: Record<string, string[]>;
        };
    };
}

export interface MutationOptions<TData, TError, TVariables> extends UseMutationOptions<TData, TError, TVariables> {
    /**
     * Si `true`, une erreur 422 déclenche une notification globale (premier message).
     * Opt-in : les formulaires qui affichent les erreurs inline doivent laisser `false`.
     */
    showValidationErrorToast?: boolean;
    /**
     * Déroge au délai d'abandon par défaut de 15 s (#24, #86).
     *
     * Réservé aux appels dont on SAIT qu'ils sont longs par nature — les appels
     * synchrones vers un LLM. La dérogation s'écrit ici, au site d'appel, et
     * jamais dans une liste centralisée : voir `axios-client.ts`.
     */
    timeoutMs?: number;
}

/**
 * Extrait un message lisible depuis une erreur de validation Laravel (422).
 * Priorité : 1er message du 1er champ > message global > null.
 */
export function formatValidationError(error: unknown): string | null {
    if (!axios.isAxiosError(error) || error.response?.status !== 422) return null;

    const data = error.response.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    const firstMessage = data?.errors ? Object.values(data.errors)[0]?.[0] : undefined;
    return firstMessage ?? data?.message ?? null;
}


/**
 * Type représentant la réponse d'une API, incluant les données, les liens et les métadonnées de pagination.
 */
export type ApiResponse<T> = {
    data: T;
    links?: {
        first: string | null;
        last: string | null;
        next: string | null;
        prev: string | null;
    };
    meta?: {
        current_page: number;
        from: number;
        last_page: number;
        links: Array<{
            active: boolean;
            label: string;
            url: string;
        }>;
        path: string;
        per_page: number;
        to: number;
        total: number;
    };
};

const isAuthError = (error: unknown): boolean => {
    return axios.isAxiosError(error) && error.response?.status === 401;
};

/**
 * Fonction utilitaire pour vérifier si une valeur est un fichier (File ou Blob).
 * @param value Valeur à vérifier.
 * @returns Booléen indiquant si la valeur est un fichier.
 */
const isFile = (value: any): value is File | Blob => {
    return typeof value === 'object' && value !== null && (value instanceof File || value instanceof Blob);
};

/**
 * Wrapper autour de `useQuery` de React Query avec gestion des erreurs d'authentification.
 * @param url URL de la requête.
 * @param config Configuration Axios pour la requête.
 * @param options Options supplémentaires pour useQuery.
 * @returns Résultat de useQuery.
 */
/**
 * Clé de query construite par useApiQuery. Tout setQueryData/invalidateQueries
 * ciblant une query de useApiQuery DOIT passer par ce builder : la convention
 * `[url, config]` n'existe qu'ici.
 */
export const apiQueryKey = (url: string, config: AxiosRequestConfig = {}) => [url, config] as const;

export function useApiQuery<TData = unknown>(
    url: string,
    config: AxiosRequestConfig = {},
    options?: UseQueryOptions<TData, ApiError>
) {
    const isEnabled = url !== ''; // Détermine si la requête doit être activée.

    return useQuery<TData, ApiError>(
        apiQueryKey(url, config), // Clé de la requête.
        // Fonction de récupération : GET de l'URL, sauf `options.queryFn` (ex. sondage incrémental qui fusionne
        // avec le cache) ; la clé reste celle de l'URL, les invalidations par apiQueryKey(url) restent valables.
        (context) => (options?.queryFn ? options.queryFn(context) : axiosClient.get(url, config).then((res) => res.data)),
        {
            ...options,
            enabled: isEnabled && options?.enabled !== false, // Combine les conditions d'activation.
            onError: (error: ApiError) => {
                if (isAuthError(error)) {
                    console.error('Authentication error, redirecting...');
                    // Ici, gérez l'erreur d'authentification globalement (ex. redirection vers la page de connexion).
                }
                // Appel au callback onError défini par l'utilisateur, s'il existe.
                options?.onError?.(error);
            },
        }
    );
}

/**
 * Wrapper autour de `useMutation` de React Query avec gestion des erreurs d'authentification.
 * @param method Méthode HTTP à utiliser ('post', 'put', 'patch', 'delete').
 * @param getUrl Fonction pour obtenir l'URL en fonction des variables.
 * @param options Options supplémentaires pour useMutation.
 * @returns Résultat de useMutation.
 */
// src/api/useApi.ts (ou le fichier approprié)

export function useApiMutation<
    TData = unknown,
    TError = ApiError,
    TVariables = any
>(
    method: 'post' | 'put' | 'patch' | 'delete',
    getUrl: (variables: TVariables) => string,
    options?: MutationOptions<TData, TError, TVariables>
) {
    const mutationFn = (variables: TVariables) => {
        const url = getUrl(variables);

        let data: any = variables;

        // Vérifier si les variables contiennent au moins un fichier
        const hasFile = variables && typeof variables === 'object' && !Array.isArray(variables)
            ? Object.values(variables).some(isFile)
            : false;

        if (hasFile) {
            data = new FormData();
            for (const key in variables) {
                const value = variables[key];
                if (value !== undefined && value !== null) {
                    if (isFile(value)) {
                        data.append(key, value);
                    } else if (typeof value === 'object') {
                        // Si la valeur est un objet non-fichier, la sérialiser
                        data.append(key, JSON.stringify(value));
                    } else {
                        // Pour les chaînes de caractères ou autres types primitifs
                        data.append(key, String(value));
                    }
                }
            }
        }

        return axiosClient.request({
            url,
            method,
            data,
            headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
            // Absent = le délai par défaut du client s'applique.
            ...(options?.timeoutMs ? { timeout: options.timeoutMs } : {}),
        }).then((res) => res.data);
    };

    return useMutation<TData, TError, TVariables>(mutationFn, {
        ...options,
        onError: (error: TError, variables: TVariables, context: any) => {
            if (isAuthError(error)) {
                console.error('Authentication error, handling...');
            }

            // Dispatch AVANT le onError du caller : si le caller appelle aussi setNotifications,
            // sa notification remplace la nôtre (le caller gagne).
            if (options?.showValidationErrorToast) {
                const message = formatValidationError(error);
                if (message) {
                    window.dispatchEvent(new CustomEvent(API_VALIDATION_ERROR_EVENT, {
                        detail: { message },
                    }));
                }
            }

            options?.onError?.(error, variables, context);
        },
    });
}

